import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { motion } from 'framer-motion'; // Import framer-motion
import SplashText from './SplashText';
import ToggleSwitch from '../ToggleSwitch';
import UseToast from '../../UseToast';
import AnimatedButtons from './AnimatedButtons';
import { Tabs, Tab, Box } from '@mui/material';
import Swal from 'sweetalert2';
import { MuiColorInput } from 'mui-color-input';
import CaptionUploadModal from '../../CaptionUploadModal';
import { useCaptionUpload } from '../../../hooks/useCaptionUpload';
import useFontFamilies from '../../../hooks/useFontFamilies';

export default function SplashContent() {
  const fontOptions = useFontFamilies('option');
  const { userId } = useParams(); // Get the optional client ID from the route

  const navigate = useNavigate();
  const { showMsgSuccessToast, showErrorToast } = UseToast(); // Use the custom hook

  // Language management
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [loadingLanguages, setLoadingLanguages] = useState(false);

  // States for four different files
  const [leftTopMedia, setLeftTopMedia] = useState(null);
  const [leftBottomMedia, setLeftBottomMedia] = useState(null);
  const [rightTopMedia, setRightTopMedia] = useState(null);
  const [rightBottomMedia, setRightBottomMedia] = useState(null);
  const [splashVideoMedia, setSplashVideoMedia] = useState(null);
  const [splashVideoTwoMedia, setSplashVideoTwoMedia] = useState(null); // New state for Splash Video 2
  const [splashLogoMedia, setSplashLogoMedia] = useState(null);
  const [closedLogoMedia, setClosedLogoMedia] = useState(null);
  const [closedVideo, setClosedVideo] = useState(null);
  const [splashText, setSplashText] = useState('');
  const [splashTextDownload, setSplashTextDownload] = useState('');
  const [buttonColor, setButtonColor] = useState('');
  const [videotime, setVideoTime] = useState(0);
  const [heightoffset, setHeightOffset] = useState(0);
  const [leftoffset, setLeftOffset] = useState(0);
  // Add this with your other state variables
  const [captionFile, setCaptionFile] = useState(null);

  const [uploadingStates, setUploadingStates] = useState({
    leftTop: false,
    leftBottom: false,
    rightTop: false,
    rightBottom: false,
    splashVideo: false,
    splashVideoTwo: false, // New state for Splash Video 2
    splashLogoMedia: false,
    closedLogoMedia: false,
    closedVideo: false,
  });
  const [uploadedUrls, setUploadedUrls] = useState({
    leftTop: {
      fileName: '',
      fileUrl: '',
      enabled: false,
    },
    leftBottom: {
      fileName: '',
      fileUrl: '',
      enabled: false,
    },
    rightTop: {
      fileName: '',
      fileUrl: '',
      enabled: false,
    },
    rightBottom: {
      fileName: '',
      fileUrl: '',
      enabled: false,
    },
    splashVideo: {
      fileName: '',
      fileUrl: '',
      enabled: false,
      captionFile: '',
     
    },
    splashVideoTwo: { // New state for Splash Video 2
      fileName: '',
      fileUrl: '',
      enabled: false,
      captionFile: '',
    },
    splashLogoMedia: {
      fileName: '',
      fileUrl: '',
      enabled: true,
       width: 0,
      height: 0,
    },
    closedLogoMedia: {
      fileName: '',
      fileUrl: '',
      enabled: true,
    },
    closedVideo: {
      fileName: '',
      fileUrl: '',
      enabled: true,
    },
  });

  const [settings, setSettings] = useState({
    userId: null,
    globalSettings: {
      height: 50,
      width: 100,
      font: 'Arial',
      fontColor: '#127E10',
      fontSize: 16,
      shadow: {
        angle: 5,
        size: 10,
        distance: 5,
        alpha: 0.5,
        color: '#FF5733', // Bright orange
      },
      highlight: {
        fontColor: '#000000',
      },
      textAlign: 'center',
    },

    enabled: false,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenVideoTwo, setIsModalOpenVideoTwo] = useState(false);




  useEffect(() => {
    if (userId) {
      fetchAvailableLanguages();
      const savedLang = localStorage.getItem(`language_pref_${userId}`) || 'en';
      setCurrentLanguage(savedLang);
      fetchMedia(userId, savedLang);
    }
  }, [userId]);

  useEffect(() => {
    if (userId && currentLanguage) {
      fetchMedia(userId, currentLanguage);
    }
  }, [currentLanguage]);

  useEffect(() => {
    if (userId) {
      fetch(`/api/splash-get-user-global/${userId}`)
        .then(res => res.json())
        .then(data => {
          console.log('data', data);
          if (!data.error) {
            setSettings(prevSettings => ({
              ...prevSettings,
              globalSettings: {
                ...prevSettings?.globalSettings,
                ...data.result?.globalSettings, // Merge globalSettings as well
              },
            }));

            setButtonColor(data?.site_brand_color);
          }
        })
        .catch(() => console.log('Button not found, creating new settings.'));
    }
  }, [userId]);

  const fetchAvailableLanguages = async () => {
    try {
      setLoadingLanguages(true);
      const response = await axios.get(`/api/language/${userId}`);
      if (response.data.success) {
        setAvailableLanguages(response.data.languages || ['en']);
        
        const savedLang = localStorage.getItem(`language_pref_${userId}`);
        if (savedLang && response.data.languages.includes(savedLang)) {
          setCurrentLanguage(savedLang);
        } else {
          setCurrentLanguage(response.data.languages[0] || 'en');
        }
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
      setAvailableLanguages(['en']);
      setCurrentLanguage('en');
    } finally {
      setLoadingLanguages(false);
    }
  };

  const fetchMedia = async (userId, lang = 'en') => {
    try {
      // Make an API call to get media data for the user with current language
      const response = await axios.get(`/api/get-splash-content/${userId}/${lang}`);
      const mediaData = response.data; // Access the first object in the array

      // Update the state with the retrieved media data
      setUploadedUrls(prevUrls => ({
        ...prevUrls, // Preserve previous data
        leftTop: {
          fileName: mediaData?.leftTop?.fileName || '',
          fileUrl: mediaData?.leftTop?.fileUrl || '',
          enabled: mediaData?.leftTop?.enabled || false,
        },
        leftBottom: {
          fileName: mediaData?.leftBottom?.fileName || '',
          fileUrl: mediaData?.leftBottom?.fileUrl || '',
          enabled: mediaData?.leftBottom?.enabled || false,
        },
        rightTop: {
          fileName: mediaData?.rightTop?.fileName || '',
          fileUrl: mediaData?.rightTop?.fileUrl || '',
          enabled: mediaData?.rightTop?.enabled || false,
        },
        rightBottom: {
          fileName: mediaData?.rightBottom?.fileName || '',
          fileUrl: mediaData?.rightBottom?.fileUrl || '',
          enabled: mediaData?.rightBottom?.enabled || false,
        },
        splashVideo: {
          fileName: mediaData?.splashVideo?.fileName || '',
          fileUrl: mediaData?.splashVideo?.fileUrl || '',
          enabled: mediaData?.splashVideo?.enabled || false,
          captionFile: mediaData?.splashVideo?.captionFile || '',
        },
        splashVideoTwo: {
          fileName: mediaData?.splashVideoTwo?.fileName || '',
          fileUrl: mediaData?.splashVideoTwo?.fileUrl || '',
          enabled: mediaData?.splashVideoTwo?.enabled || false,
          captionFile: mediaData?.splashVideoTwo?.captionFile || '',
        },
        splashLogoMedia: {
          fileName: mediaData?.splashLogoMedia?.fileName || '',
          fileUrl: mediaData?.splashLogoMedia?.fileUrl || '', 
          enabled: mediaData?.splashLogoMedia?.enabled || false,
          width: mediaData?.splashLogoMedia?.width || 0,  
          height: mediaData?.splashLogoMedia?.height || 0,
        },
        closedLogoMedia: {
          fileName: mediaData?.closedLogoMedia?.fileName || '',
          fileUrl: mediaData?.closedLogoMedia?.fileUrl || '',
          enabled: mediaData?.closedLogoMedia?.enabled || false,
        },
        closedVideo: {
          fileName: mediaData?.closedVideo?.fileName || '',
          fileUrl: mediaData?.closedVideo?.fileUrl || '',
          enabled: mediaData?.closedVideo?.enabled || false,
        },
      }));

      setSplashText(mediaData?.splashText ?? '');
      setSplashTextDownload(mediaData?.splashTextDownload ?? ''); // Default to an empty string only if splashText is null or undefined
      setVideoTime(mediaData?.videotime || 0);
      setHeightOffset(mediaData?.heightoffset);
      setLeftOffset(mediaData?.leftoffset);
    } catch (error) {
      console.error('Failed to fetch media data:', error);
    }
  };

  // Drop handlers for different media
  const createDropHandler = setMedia => acceptedFiles => {
    if (acceptedFiles.length > 0) {
      setMedia(acceptedFiles[0]);
    }
  };

  // Common upload handler
  const handleUpload = async (file, position) => {
    if (!file) {
      showErrorToast(`Please select a file for ${position} first!`);
      return;
    }

    // Set the specific position to uploading
    setUploadingStates(prev => ({ ...prev, [position]: true }));

    try {
      // Step 1: Get Pre-signed URL from the Backend
      const response = await axios.get('/generate-signed-url', {
        params: {
          fileName: file.name,
          fileType: file.type,
        },
      });

      const { url } = response.data;

      // Step 2: Upload the File to S3
      await axios.put(url, file, {
        headers: {
          'Content-Type': file.type,
        },
      });

      // Step 3: Save Uploaded File Information
      const fileUrl = url.split('?')[0];

      setUploadedUrls(prev => ({
        ...prev,
        [position]: {
          fileName: file.name, // Store file name
          fileUrl: fileUrl, // Store file URL
        },
      }));
      setUploadingStates(prev => ({ ...prev, [position]: false }));

      setUploadedUrls(prevState => ({
        ...prevState,
        [position]: {
          ...prevState[position],
          enabled: true,
        },
      }));
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    settings.userId = userId;
    try {
      await axios.post('/api/save-splash-content', {
        userId,
        mediaData: uploadedUrls,
        splashText: splashText,
        splashTextDownload: splashTextDownload,
        videotime: videotime,
        heightoffset: heightoffset,
        leftoffset: leftoffset,
        language: currentLanguage, // Add current language
      });

      if (settings?.enabled) {
        await fetch(`/api/splash-save-user-global-settings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(settings),
        });
      }

      showMsgSuccessToast('Settings Save Successfully!');
    } catch (error) {
      console.error('Failed to save media data:', error);
      showErrorToast('Error saving media data!');
    }
  };

  const handleToggleChange = async isOn => {
    console.log('Toggle state:', isOn);
    setSettings(prevSettings => ({
      ...prevSettings,
      enabled: isOn, // Update the 'enabled' setting
    }));

    settings.enabled = isOn;
    settings.userId = userId;
  };

  const handleChange = (key, value) => {
    // Determine whether to update global or custom settings
    const basePath = 'globalSettings';
    const fullPath = `${basePath}.${key}`;
    const keys = fullPath.split('.'); // Split the path into parts
    const newSettings = { ...settings };

    keys.reduce((obj, currentKey, index) => {
      if (index === keys.length - 1) {
        // If it's the last key, update the value
        obj[currentKey] = value;
      } else {
        // Otherwise, ensure the object exists before traversing
        obj[currentKey] = { ...obj[currentKey] };
      }
      return obj[currentKey];
    }, newSettings);

    setSettings(newSettings);
  };

  const handleToggleChangeMedia = (mediaKey, on) => {
    setUploadedUrls(prevState => ({
      ...prevState,
      [mediaKey]: {
        ...prevState[mediaKey],
        enabled: on,
      },
    }));
  };

  const [value, setValue] = useState(1);

  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };

  const escapeHtml = unsafe => {
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const deleteFile = async (fileNameurl, key) => {
    const decodedUrl = decodeURIComponent(fileNameurl); // Decodes %20 to spaces
    const fileName = decodedUrl.split('/').pop(); // Takes last segment after /
    // Show confirmation dialog

    const result = await Swal.fire({
      title: 'Are you sure?',
      html: `You are about to delete <b>${escapeHtml(fileName)}</b>. <br> This action cannot be undo!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: buttonColor,
      confirmButtonText: '<i class="fa fa-thumbs-up"></i> Yes, delete it!',
      cancelButtonText: '<i class="fa fa-thumbs-down"></i> Cancel',
    });

    // If user confirms deletion
    if (result.isConfirmed) {
      Swal.fire({
        title: 'Deleting...',
        text: 'Please wait while we delete the file.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading(); // Show loading spinner
        },
      });
      try {
        // Call your backend API to delete the file
        const response = await axios.post('/api/delete-splash-file', {
          fileName: fileName,
          userId: userId,
          position: key,
        });

        if (response.data.success) {
          // Update the state to remove the file
          setUploadedUrls(prev => ({
            ...prev,
            [key]: {
              fileName: '',
              fileUrl: '',
              enabled: false,
            },
          }));

          // Show success message
          Swal.fire('Deleted!', 'Your file has been deleted.', 'success');
        } else {
          Swal.fire('Error!', 'Failed to delete file.', 'error');
        }
      } catch (error) {
        console.error('Error deleting file:', error);
        Swal.fire('Error!', 'An error occurred while deleting the file.', 'error');
      }
    }
  };

  /////////////////-------------------------.//////////////////////////
  // Add this with your other drop handlers


  const handleCaptionDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];

      if (file.name.endsWith(".srt") || file.name.endsWith(".vtt")) {
        setCaptionFile(file);
      } else {
        showErrorToast("Please select a .srt or .vtt file for captions!");
      }
    }
  };

  function convertSrtToVtt(srtContent) {
    try {
      // Validate input
      if (!srtContent || typeof srtContent !== 'string') {
        throw new Error('Invalid SRT content');
      }

      // Remove BOM if present
      srtContent = srtContent.replace(/^\uFEFF/, '');

      // Convert SRT timing format to VTT timing format
      // SRT: 00:00:01,600 --> 00:00:04,200
      // VTT: 00:00:01.600 --> 00:00:04.200
      let vttContent = srtContent
        .replace(/\r\n/g, '\n') // Normalize line endings
        .replace(/\r/g, '\n')
        .replace(/\n+/g, '\n')
        .trim();

      // Add VTT header
      vttContent = 'WEBVTT\n\n' + vttContent;

      // Convert timing format: comma to dot for milliseconds
      vttContent = vttContent.replace(
        /(\d{2}:\d{2}:\d{2}),(\d{3})/g,
        '$1.$2'
      );

      // Remove SRT index numbers (optional)
      vttContent = vttContent.replace(
        /^\d+\s*$/gm,
        ''
      );

      // Clean up empty lines
      vttContent = vttContent
        .split('\n')
        .filter(line => line.trim() !== '')
        .join('\n');

      return vttContent;
    } catch (error) {
      console.error('Error converting SRT to VTT:', error);
      throw new Error(`Failed to convert SRT to VTT: ${error.message}`);
    }
  }

  const { handleCaptionUpload: uploadCaption } = useCaptionUpload();

  // Update the modal call
  const handleCaptionUpload = async (captionFile, convertSrtToVtt, videoKey) => {
    try {
      await uploadCaption(
        captionFile,
        convertSrtToVtt,
        setUploadedUrls,
        videoKey,
        userId
      );
      setCaptionFile(null);
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  // Function to change language
  const handleLanguageChange = async (langCode) => {
    setCurrentLanguage(langCode);
    localStorage.setItem(`language_pref_${userId}`, langCode);
    await fetchMedia(userId, langCode);
    showMsgSuccessToast(`Language changed to ${getLanguageName(langCode)}`);
  };

  // Helper function to get language name
  const getLanguageName = (code) => {
    const languageNames = {
      en: 'English',
      es: 'Spanish',
      fr: 'French',
      de: 'German',
      it: 'Italian',
      pt: 'Portuguese',
      ru: 'Russian',
      zh: 'Chinese',
      ja: 'Japanese',
      ko: 'Korean',
      ar: 'Arabic',
      hi: 'Hindi',
      tr: 'Turkish',
      nl: 'Dutch',
      sv: 'Swedish',
      pl: 'Polish',
      th: 'Thai',
      vi: 'Vietnamese',
      id: 'Indonesian',
    };
    return languageNames[code] || code.toUpperCase();
  };

  // Helper function to get language flag
  const getLanguageFlag = (code) => {
    const flagMap = {
      en: 'us',
      es: 'es',
      fr: 'fr',
      de: 'de',
      it: 'it',
      pt: 'pt',
      ru: 'ru',
      zh: 'cn',
      ja: 'jp',
      ko: 'kr',
      ar: 'sa',
      hi: 'in',
      tr: 'tr',
      nl: 'nl',
      sv: 'se',
      pl: 'pl',
      th: 'th',
      vi: 'vn',
      id: 'id',
    };
    return flagMap[code] || 'xx';
  };


  return (
    <div className="main-container">
      <div className="pd-ltr-20 xs-pd-20-10">
        <div className="min-height-200px dashboard-container">
          <div className="page-header">
            <div className="row">
              <div className="col-md-6 col-sm-12">
                <div className="title">
                  <h4>Button Splash Screen Settings</h4>
                </div>
              </div>

              {/* Language Switcher */}
              <div className="col-md-3 col-sm-12 d-flex align-items-center justify-content-end">
                {loadingLanguages ? (
                  <div className="d-flex align-items-center">
                    <div
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span>Loading languages...</span>
                  </div>
                ) : availableLanguages.length > 1 ? (
                  <div className="language-switcher">
                    <div className="dropdown">
                      <button
                        className="btn btn-outline-secondary dropdown-toggle d-flex align-items-center"
                        type="button"
                        id="languageDropdownSplash"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        style={{
                          borderColor: buttonColor,
                          color: buttonColor,
                          backgroundColor: 'transparent',
                        }}
                      >
                        <span
                          className={`fi fi-${getLanguageFlag(currentLanguage)} me-2`}
                          style={{ fontSize: '16px' }}
                        ></span>
                        <span className="me-1">
                          {getLanguageName(currentLanguage)}
                        </span>
                        <span
                          className="badge bg-primary ms-1"
                          style={{ backgroundColor: buttonColor }}
                        >
                          {availableLanguages.length}
                        </span>
                      </button>
                      <ul
                        className="dropdown-menu"
                        aria-labelledby="languageDropdownSplash"
                      >
                        {availableLanguages.map((lang) => (
                          <li key={lang}>
                            <button
                              className={`dropdown-item d-flex align-items-center ${currentLanguage === lang ? 'active' : ''}`}
                              onClick={() => handleLanguageChange(lang)}
                              style={{
                                backgroundColor:
                                  currentLanguage === lang
                                    ? `${buttonColor}20`
                                    : 'transparent',
                              }}
                            >
                              <span
                                className={`fi fi-${getLanguageFlag(lang)} me-2`}
                                style={{ fontSize: '16px' }}
                              ></span>
                              <span>{getLanguageName(lang)}</span>
                              {currentLanguage === lang && (
                                <i className="fas fa-check ms-auto text-success"></i>
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : availableLanguages.length === 1 ? (
                  <div className="d-flex align-items-center text-muted">
                    <span
                      className={`fi fi-${getLanguageFlag(currentLanguage)} me-2`}
                      style={{ fontSize: '16px' }}
                    ></span>
                    <span>{getLanguageName(currentLanguage)}</span>
                    <small className="ms-2">(Only language available)</small>
                  </div>
                ) : null}
              </div>

              <div className="col-md-3 col-sm-12 text-end">
                <button
                  className="btn btn-outline-secondary btn-back-effect"
                  onClick={() => navigate(-1)}
                  style={{
                    '--brand-color': buttonColor,
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Back
                </button>
              </div>
            </div>
          </div>
          <Box sx={{ width: '100%' }}>
            <Tabs
              value={value}
              onChange={handleTabChange}
              aria-label="tab navigation"
              sx={{
                '& .MuiTab-root': { minWidth: 100 }, // Ensures consistent width
                '& .Mui-selected': { fontWeight: 'bold' }, // Active tab styling
              }}
              style={{ marginBottom: '36px' }}
            >
              <Tab
                onClick={() => navigate(`/view-button-settings/${userId}`)}
                sx={{
                  '&:hover': {
                    backgroundColor: buttonColor, // Add your desired hover color here
                    color: '#fff',
                    fontWeight: 'bold',
                  },
                }}
                label="Menu Settings"
              />

              <Tab
                onClick={() => navigate(`/view-splash-settings/${userId}`)}
                sx={{
                  '&:hover': {
                    backgroundColor: buttonColor, // Add your desired hover color here
                    color: '#fff',
                    fontWeight: 'bold',
                  },
                }}
                label={'Splash Settings'}
              />
              <Tab
                onClick={() => navigate(`/global-settings/${userId}`)}
                sx={{
                  '&:hover': {
                    backgroundColor: buttonColor, // Add your desired hover color here
                    color: '#fff',
                    fontWeight: 'bold',
                  },
                }}
                label={'Global Settings'}
              />
              <Tab
                onClick={() => navigate(`/availablity-settings/${userId}`)}
                sx={{
                  '&:hover': {
                    backgroundColor: buttonColor, // Add your desired hover color here
                    color: '#fff',
                    fontWeight: 'bold',
                  },
                }}
                label={'System Settings'}
              />
            </Tabs>

            {/* <AnimatedButtons /> */}

            <form
              className="tab-wizard wizard-circle wizard vertical"
              onSubmit={handleSubmit}
              style={{ padding: '24px' }}
            >
              <div className="row ">
                {/* Left Top Media */}
                <div className="col-md-6 d-flex p-3">
                  <div
                    className={`form-group menu-card2 ${!uploadedUrls?.leftTop?.enabled ? 'disabled' : ''} h-100 w-100`}
                    style={{
                      height: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '5px',
                    }}
                  >
                    <div className="form-group">
                      {' '}
                      <label>Upper Left Media:</label>
                      <ToggleSwitch
                        label="Enable"
                        isOn={uploadedUrls.leftTop.enabled}
                        id={'leftTop'}
                        onChange={value => handleToggleChangeMedia('leftTop', value)}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                      {' '}
                      <MediaDropzone
                        onDrop={createDropHandler(setLeftTopMedia)}
                        selectedFile={leftTopMedia}
                      />
                      <UploadedUrlImage url={uploadedUrls.leftTop} />
                    </div>

                    <UploadedUrl
                      url={uploadedUrls.leftTop}
                      buttonColor={buttonColor}
                      deleteFile={() => deleteFile(uploadedUrls?.leftTop?.fileName, 'leftTop')}
                    />

                    <div>
                      <UploadButton
                        uploading={uploadingStates.leftTop}
                        onUpload={() => handleUpload(leftTopMedia, 'leftTop')}
                        buttonColor={buttonColor}
                      />
                    </div>
                  </div>
                </div>

                {/* Left Bottom Media */}
                {/* <div className="col-md-6 ">
                  <div
                    className={`form-group menu-card2 ${!uploadedUrls?.leftBottom?.enabled ? 'disabled' : ''}`}
                  >
                    <label>Left Bottom Media:</label>
                    <ToggleSwitch
                      label="Enable"
                      isOn={uploadedUrls.leftBottom.enabled}
                      id={'leftBottom'}
                      onChange={value => handleToggleChangeMedia('leftBottom', value)}
                    />
                    <MediaDropzone
                      onDrop={createDropHandler(setLeftBottomMedia)}
                      selectedFile={leftBottomMedia}
                    />
                    <UploadButton
                      uploading={uploadingStates.leftBottom}
                      onUpload={() => handleUpload(leftBottomMedia, 'leftBottom')}
                      buttonColor={buttonColor}
                    />
                    <UploadedUrl
                      url={uploadedUrls.leftBottom}
                      buttonColor={buttonColor}
                      deleteFile={() =>
                        deleteFile(uploadedUrls?.leftBottom?.fileName, 'leftBottom')
                      }
                    />
                  </div>
                </div> */}

                <div className="col-md-6 d-flex p-3">
                  <div
                    className={`form-group menu-card2 ${!uploadedUrls?.leftBottom?.enabled ? 'disabled' : ''
                      } h-100 w-100`}
                    style={{
                      height: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '5px',
                    }}
                  >
                    {/* LABEL + TOGGLE */}
                    <div className="form-group">
                      <label>Left Bottom Media:</label>
                      <ToggleSwitch
                        label="Enable"
                        isOn={uploadedUrls.leftBottom.enabled}
                        id="leftBottom"
                        onChange={value => handleToggleChangeMedia('leftBottom', value)}
                      />
                    </div>

                    {/* DROPZONE + PREVIEW (2 COLUMN GRID) */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                      <MediaDropzone
                        onDrop={createDropHandler(setLeftBottomMedia)}
                        selectedFile={leftBottomMedia}
                      />

                      <UploadedUrlImage url={uploadedUrls.leftBottom} />
                    </div>

                    {/* URL + DELETE BUTTON SECTION */}
                    <UploadedUrl
                      url={uploadedUrls.leftBottom}
                      buttonColor={buttonColor}
                      deleteFile={() =>
                        deleteFile(uploadedUrls?.leftBottom?.fileName, 'leftBottom')
                      }
                    />

                    {/* UPLOAD BUTTON */}
                    <div>
                      <UploadButton
                        uploading={uploadingStates.leftBottom}
                        onUpload={() => handleUpload(leftBottomMedia, 'leftBottom')}
                        buttonColor={buttonColor}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Top Media */}
              <div className="row ">
                {/* <div className="col-md-6 ">
                  <div
                    className={`form-group menu-card2 ${!uploadedUrls?.rightTop?.enabled ? 'disabled' : ''}`}
                  >
                    <label>Right Top Media:</label>
                    <ToggleSwitch
                      label="Enable"
                      isOn={uploadedUrls.rightTop.enabled}
                      id={'rightTop'}
                      onChange={value => handleToggleChangeMedia('rightTop', value)}
                    />
                    <MediaDropzone
                      onDrop={createDropHandler(setRightTopMedia)}
                      selectedFile={rightTopMedia}
                    />
                    <UploadButton
                      uploading={uploadingStates.rightTop}
                      onUpload={() => handleUpload(rightTopMedia, 'rightTop')}
                      buttonColor={buttonColor}
                    />
                    <UploadedUrl
                      url={uploadedUrls.rightTop}
                      buttonColor={buttonColor}
                      deleteFile={() => deleteFile(uploadedUrls?.rightTop?.fileName, 'rightTop')}
                    />
                  </div>
                </div> */}

                <div className="col-md-6 d-flex p-3">
                  <div
                    className={`form-group menu-card2 ${!uploadedUrls?.rightTop?.enabled ? 'disabled' : ''} h-100 w-100`}
                    style={{
                      height: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '5px',
                    }}
                  >
                    <div className="form-group">
                      <label>Right Top Media:</label>
                      <ToggleSwitch
                        label="Enable"
                        isOn={uploadedUrls.rightTop.enabled}
                        id="rightTop"
                        onChange={value => handleToggleChangeMedia('rightTop', value)}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                      <MediaDropzone
                        onDrop={createDropHandler(setRightTopMedia)}
                        selectedFile={rightTopMedia}
                      />
                      <UploadedUrlImage url={uploadedUrls.rightTop} />
                    </div>

                    <UploadedUrl
                      url={uploadedUrls.rightTop}
                      buttonColor={buttonColor}
                      deleteFile={() => deleteFile(uploadedUrls?.rightTop?.fileName, 'rightTop')}
                    />

                    <div>
                      <UploadButton
                        uploading={uploadingStates.rightTop}
                        onUpload={() => handleUpload(rightTopMedia, 'rightTop')}
                        buttonColor={buttonColor}
                      />
                    </div>
                  </div>
                </div>

                {/* Right Bottom Media */}
                {/* <div className="col-md-6 ">
                  <div
                    className={`form-group menu-card2 ${!uploadedUrls?.rightBottom?.enabled ? 'disabled' : ''}`}
                  >
                    <label>Right Bottom Media:</label>
                    <ToggleSwitch
                      label="Enable"
                      isOn={uploadedUrls.rightBottom.enabled}
                      id={'rightBottom'}
                      onChange={value => handleToggleChangeMedia('rightBottom', value)}
                    />
                    <MediaDropzone
                      onDrop={createDropHandler(setRightBottomMedia)}
                      selectedFile={rightBottomMedia}
                    />
                    <UploadButton
                      uploading={uploadingStates.rightBottom}
                      onUpload={() => handleUpload(rightBottomMedia, 'rightBottom')}
                      buttonColor={buttonColor}
                    />
                    <UploadedUrl
                      url={uploadedUrls.rightBottom}
                      buttonColor={buttonColor}
                      deleteFile={() =>
                        deleteFile(uploadedUrls?.rightBottom?.fileName, 'rightBottom')
                      }
                    />
                  </div>
                </div> */}

                <div className="col-md-6 d-flex p-3">
                  <div
                    className={`form-group menu-card2 ${!uploadedUrls?.rightBottom?.enabled ? 'disabled' : ''} h-100 w-100`}
                    style={{
                      height: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '5px',
                    }}
                  >
                    <div className="form-group">
                      <label>Right Bottom Media:</label>
                      <ToggleSwitch
                        label="Enable"
                        isOn={uploadedUrls.rightBottom.enabled}
                        id="rightBottom"
                        onChange={value => handleToggleChangeMedia('rightBottom', value)}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                      <MediaDropzone
                        onDrop={createDropHandler(setRightBottomMedia)}
                        selectedFile={rightBottomMedia}
                      />
                      <UploadedUrlImage url={uploadedUrls.rightBottom} />
                    </div>

                    <UploadedUrl
                      url={uploadedUrls.rightBottom}
                      buttonColor={buttonColor}
                      deleteFile={() =>
                        deleteFile(uploadedUrls?.rightBottom?.fileName, 'rightBottom')
                      }
                    />

                    <div>
                      <UploadButton
                        uploading={uploadingStates.rightBottom}
                        onUpload={() => handleUpload(rightBottomMedia, 'rightBottom')}
                        buttonColor={buttonColor}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row ">
                {/* <div className="col-md-6 ">
                  <div
                    className={`form-group menu-card2 ${!uploadedUrls?.splashVideo?.enabled ? 'disabled' : ''}`}
                  >
                    <label>Splash video:</label>
                    <ToggleSwitch
                      label="Enable"
                      isOn={uploadedUrls.splashVideo.enabled}
                      id={'splashVideo'}
                      onChange={value => handleToggleChangeMedia('splashVideo', value)}
                    />
                    <MediaDropzone
                      onDrop={createDropHandler(setSplashVideoMedia)}
                      selectedFile={splashVideoMedia}
                    />
                    <UploadButton
                      uploading={uploadingStates.splashVideo}
                      onUpload={() => handleUpload(splashVideoMedia, 'splashVideo')}
                      buttonColor={buttonColor}
                    />
                    <UploadedUrl
                      url={uploadedUrls.splashVideo}
                      buttonColor={buttonColor}
                      deleteFile={() =>
                        deleteFile(uploadedUrls?.splashVideo?.fileName, 'splashVideo')
                      }
                    />
                  </div>
                </div>

                <div className="col-md-6 ">
                  <div
                    className={`form-group menu-card2 ${!uploadedUrls?.splashLogoMedia?.enabled ? 'disabled' : ''}`}
                  >
                    <label>Splash Logo:</label>
                    <ToggleSwitch
                      label="Enable"
                      isOn={uploadedUrls.splashLogoMedia.enabled}
                      id={'splashLogoMedia'}
                      onChange={value => handleToggleChangeMedia('splashLogoMedia', value)}
                    />
                    <MediaDropzone
                      onDrop={createDropHandler(setSplashLogoMedia)}
                      selectedFile={splashLogoMedia}
                    />
                    <UploadButton
                      uploading={uploadingStates.splashLogoMedia}
                      onUpload={() => handleUpload(splashLogoMedia, 'splashLogoMedia')}
                      buttonColor={buttonColor}
                    />
                    <UploadedUrl
                      url={uploadedUrls.splashLogoMedia}
                      buttonColor={buttonColor}
                      deleteFile={() =>
                        deleteFile(uploadedUrls?.splashLogoMedia?.fileName, 'splashLogoMedia')
                      }
                    />
                  </div>
                </div> */}

                <div className="col-md-6 d-flex p-3">
                  <div
                    className={`form-group menu-card2 ${!uploadedUrls?.splashVideo?.enabled ? 'disabled' : ''} h-100 w-100`}
                    style={{
                      height: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '5px',
                    }}
                  >
                    <div className="form-group">
                      <label>Splash Video:</label>
                      <ToggleSwitch
                        label="Enable"
                        isOn={uploadedUrls.splashVideo.enabled}
                        id="splashVideo"
                        onChange={value => handleToggleChangeMedia('splashVideo', value)}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                      <MediaDropzone
                        onDrop={createDropHandler(setSplashVideoMedia)}
                        selectedFile={splashVideoMedia}
                      />
                      <UploadedUrlImage url={uploadedUrls.splashVideo} />
                    </div>

                    <UploadedUrl
                      url={uploadedUrls.splashVideo}
                      buttonColor={buttonColor}
                      deleteFile={() =>
                        deleteFile(uploadedUrls?.splashVideo?.fileName, 'splashVideo')
                      }
                    />

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                      <UploadButton
                        uploading={uploadingStates.splashVideo}
                        onUpload={() => handleUpload(splashVideoMedia, 'splashVideo')}
                        buttonColor={buttonColor}
                      />
                      {/* Caption indicator and button */}
                      <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <button
                          type="button"
                          onClick={() => setIsModalOpen(true)}
                          style={{
                            backgroundColor: buttonColor,
                            color: '#ffffffff',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            padding: '6px 12px',
                            cursor: 'pointer',
                            fontSize: '15px',
                          }}
                        >
                          {uploadedUrls.splashVideo.captionFile ? 'Manage Captions' : 'Upload Captions'}
                        </button>

                        {uploadedUrls.splashVideo.captionFile && (
                          <span style={{ fontSize: '15px', color: '#252725ff', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '700' }}>
                            <i className="bi bi-cc-circle"></i>
                            Captions Available
                          </span>
                        )}
                        <CaptionUploadModal
                          isOpen={isModalOpen}
                          onClose={() => setIsModalOpen(false)}
                          onUpload={(captionFile, convertSrtToVtt) =>
                            handleCaptionUpload(captionFile, convertSrtToVtt, 'splashVideo')
                          }
                          existingCaptionUrl={uploadedUrls.splashVideo.captionFile}
                          buttonColor={buttonColor}
                          showMsgSuccessToast={showMsgSuccessToast}
                          showErrorToast={showErrorToast}
                        />
                      </div>
                    </div>
                  </div>


                </div>

                {/* Splash Video 2 Section */}
                <div className="col-md-6 d-flex p-3">
                  <div
                    className={`form-group menu-card2 ${!uploadedUrls?.splashVideoTwo?.enabled ? 'disabled' : ''} h-100 w-100`}
                    style={{
                      height: 'auto',
                    }}
                  >
                    <div className="form-group">
                      <label>Splash Video 2:</label>
                      <ToggleSwitch
                        label="Enable"
                        isOn={uploadedUrls.splashVideoTwo.enabled}
                        id="splashVideoTwo"
                        onChange={value => handleToggleChangeMedia('splashVideoTwo', value)}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                      <MediaDropzone
                        onDrop={createDropHandler(setSplashVideoTwoMedia)}
                        selectedFile={splashVideoTwoMedia}
                      />
                      <UploadedUrlImage url={uploadedUrls.splashVideoTwo} />
                    </div>

                    <UploadedUrl
                      url={uploadedUrls.splashVideoTwo}
                      buttonColor={buttonColor}
                      deleteFile={() =>
                        deleteFile(uploadedUrls?.splashVideoTwo?.fileName, 'splashVideoTwo')
                      }
                    />

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                      <UploadButton
                        uploading={uploadingStates.splashVideoTwo}
                        onUpload={() => handleUpload(splashVideoTwoMedia, 'splashVideoTwo')}
                        buttonColor={buttonColor}
                      />
                      {/* Caption indicator and button */}
                      <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <button
                          type="button"
                          onClick={() => setIsModalOpenVideoTwo(true)}
                          style={{
                            backgroundColor: buttonColor,
                            color: '#ffffffff',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            padding: '6px 12px',
                            cursor: 'pointer',
                            fontSize: '15px',
                          }}
                        >
                          {uploadedUrls.splashVideoTwo.captionFile ? 'Manage Captions' : 'Upload Captions'}
                        </button>

                        {uploadedUrls.splashVideoTwo.captionFile && (
                          <span style={{ fontSize: '15px', color: '#252725ff', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '700' }}>
                            <i className="bi bi-cc-circle"></i>
                            Captions Available
                          </span>
                        )}
                        <CaptionUploadModal
                          isOpen={isModalOpenVideoTwo}
                          onClose={() => setIsModalOpenVideoTwo(false)}
                          onUpload={(captionFile, convertSrtToVtt) =>
                            handleCaptionUpload(captionFile, convertSrtToVtt, 'splashVideoTwo')
                          }
                          existingCaptionUrl={uploadedUrls.splashVideoTwo.captionFile}
                          buttonColor={buttonColor}
                          showMsgSuccessToast={showMsgSuccessToast}
                          showErrorToast={showErrorToast}
                        />
                      </div>
                    </div>
                  </div>


                </div>

                <div className="col-md-6 d-flex p-3">
                  <div
                    className={`form-group menu-card2 ${!uploadedUrls?.splashLogoMedia?.enabled ? 'disabled' : ''} h-100 w-100`}
                    style={{
                      height: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '5px',
                    }}
                  >
                    <div className="form-group">
                      <label>Splash Logo:</label>
                      <ToggleSwitch
                        label="Enable"
                        isOn={uploadedUrls.splashLogoMedia.enabled}
                        id="splashLogoMedia"
                        onChange={value => handleToggleChangeMedia('splashLogoMedia', value)}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                      <MediaDropzone
                        onDrop={createDropHandler(setSplashLogoMedia)}
                        selectedFile={splashLogoMedia}
                      />
                      <UploadedUrlImage url={uploadedUrls.splashLogoMedia} />
                    </div>

                    <UploadedUrl
                      url={uploadedUrls.splashLogoMedia}
                      buttonColor={buttonColor}
                      deleteFile={() =>
                        deleteFile(uploadedUrls?.splashLogoMedia?.fileName, 'splashLogoMedia')
                      }
                    />

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <UploadButton
                        uploading={uploadingStates.splashLogoMedia}
                        onUpload={() => handleUpload(splashLogoMedia, 'splashLogoMedia')}
                        buttonColor={buttonColor}
                      />
                        <div
                              style={{
                                // flex: '1',
                                display: 'flex',
                                justifyContent: 'end',
                                alignItems: 'center',
                                height: '100%',
                              }}
                            >
                              <div
                                style={{
                                  width: '60%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  height: '100%',
                                }}
                              >
                                <label style={{ fontWeight: '600', whiteSpace: 'nowrap', margin: '0' }}>
                                  Size (%):
                                </label>

                                <input
                                  style={{ width: '100%', height: '100%' }}
                                  type="number"
                                  value={uploadedUrls.splashLogoMedia.width}
                                  onChange={e =>
                                    setUploadedUrls({
                                      ...uploadedUrls,
                                      splashLogoMedia: {
                                        ...uploadedUrls.splashLogoMedia,
                                        width: Number(e.target.value),
                                      },
                                    })
                                  }
                                />
                              </div>

                              <label style={{ display: 'none' }}>
                                Height:
                                <input
                                  type="number"
                                  value={uploadedUrls.splashLogoMedia.height}
                                  onChange={e =>
                                    setUploadedUrls({
                                      ...uploadedUrls,
                                      splashLogoMedia: {
                                        ...uploadedUrls.splashLogoMedia,
                                        height: Number(e.target.value),
                                      },
                                    })
                                  }
                                />
                              </label>
                            </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row ">
                {/* <div className="col-md-6 ">
                  <div
                    className={`form-group menu-card2 ${!uploadedUrls?.closedLogoMedia?.enabled ? 'disabled' : ''}`}
                  >
                    <label>Offline Logo:</label>
                    <ToggleSwitch
                      label="Enable"
                      isOn={uploadedUrls.closedLogoMedia.enabled}
                      id={'closedLogoMedia'}
                      onChange={value => handleToggleChangeMedia('closedLogoMedia', value)}
                    />
                    <MediaDropzone
                      onDrop={createDropHandler(setClosedLogoMedia)}
                      selectedFile={closedLogoMedia}
                    />
                    <UploadButton
                      uploading={uploadingStates.closedLogoMedia}
                      onUpload={() => handleUpload(closedLogoMedia, 'closedLogoMedia')}
                      buttonColor={buttonColor}
                    />
                    <UploadedUrl
                      url={uploadedUrls.closedLogoMedia}
                      buttonColor={buttonColor}
                      deleteFile={() =>
                        deleteFile(uploadedUrls?.closedLogoMedia?.fileName, 'closedLogoMedia')
                      }
                    />
                  </div>
                </div>

                <div className="col-md-6 ">
                  <div
                    className={`form-group menu-card2 ${!uploadedUrls?.closedVideo?.enabled ? 'disabled' : ''}`}
                  >
                    <label>Closed Video:</label>
                    <ToggleSwitch
                      label="Enable"
                      isOn={uploadedUrls.closedVideo.enabled}
                      id={'closedVideo'}
                      onChange={value => handleToggleChangeMedia('closedVideo', value)}
                    />
                    <MediaDropzone
                      onDrop={createDropHandler(setClosedVideo)}
                      selectedFile={closedVideo}
                    />
                    <UploadButton
                      uploading={uploadingStates.closedVideo}
                      onUpload={() => handleUpload(closedVideo, 'closedVideo')}
                      buttonColor={buttonColor}
                    />
                    <UploadedUrl
                      url={uploadedUrls.closedVideo}
                      buttonColor={buttonColor}
                      deleteFile={() =>
                        deleteFile(uploadedUrls?.closedVideo?.fileName, 'closedVideo')
                      }
                    />
                  </div>
                </div> */}

                <div className="col-md-6 d-flex p-3">
                  <div
                    className={`form-group menu-card2 ${!uploadedUrls?.closedLogoMedia?.enabled ? 'disabled' : ''} h-100 w-100`}
                    style={{
                      height: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '5px',
                    }}
                  >
                    <div className="form-group">
                      <label>Offline Logo:</label>
                      <ToggleSwitch
                        label="Enable"
                        isOn={uploadedUrls.closedLogoMedia.enabled}
                        id="closedLogoMedia"
                        onChange={value => handleToggleChangeMedia('closedLogoMedia', value)}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                      <MediaDropzone
                        onDrop={createDropHandler(setClosedLogoMedia)}
                        selectedFile={closedLogoMedia}
                      />
                      <UploadedUrlImage url={uploadedUrls.closedLogoMedia} />
                    </div>

                    <UploadedUrl
                      url={uploadedUrls.closedLogoMedia}
                      buttonColor={buttonColor}
                      deleteFile={() =>
                        deleteFile(uploadedUrls?.closedLogoMedia?.fileName, 'closedLogoMedia')
                      }
                    />

                    <div>
                      <UploadButton
                        uploading={uploadingStates.closedLogoMedia}
                        onUpload={() => handleUpload(closedLogoMedia, 'closedLogoMedia')}
                        buttonColor={buttonColor}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-md-6 d-flex p-3">
                  <div
                    className={`form-group menu-card2 ${!uploadedUrls?.closedVideo?.enabled ? 'disabled' : ''} h-100 w-100`}
                    style={{
                      height: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '5px',
                    }}
                  >
                    <div className="form-group">
                      <label>Closed Video:</label>
                      <ToggleSwitch
                        label="Enable"
                        isOn={uploadedUrls.closedVideo.enabled}
                        id="closedVideo"
                        onChange={value => handleToggleChangeMedia('closedVideo', value)}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                      <MediaDropzone
                        onDrop={createDropHandler(setClosedVideo)}
                        selectedFile={closedVideo}
                      />
                      <UploadedUrlImage url={uploadedUrls.closedVideo} />
                    </div>

                    <UploadedUrl
                      url={uploadedUrls.closedVideo}
                      buttonColor={buttonColor}
                      deleteFile={() =>
                        deleteFile(uploadedUrls?.closedVideo?.fileName, 'closedVideo')
                      }
                    />

                    <div>
                      <UploadButton
                        uploading={uploadingStates.closedVideo}
                        onUpload={() => handleUpload(closedVideo, 'closedVideo')}
                        buttonColor={buttonColor}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 ">
                  <div className="form-group menu-card2">
                    <label>Splash Text:</label>
                    <input
                      type="text"
                      value={splashText}
                      onChange={e => setSplashText(e.target.value)}
                      placeholder="Enter splash text"
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group menu-card2">
                    <label>Splash Text Before Videos Download:</label>
                    <input
                      type="text"
                      value={splashTextDownload}
                      onChange={e => setSplashTextDownload(e.target.value)}
                      placeholder="Enter splash text before videos download"
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group menu-card2">
                    <label>Height Offset From Top in % :</label>
                    <input
                      type="number"
                      value={heightoffset}
                      onChange={e => setHeightOffset(Number(e.target.value))}
                      placeholder="Enter Height Offset From Top"
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group menu-card2">
                    <label>Left Offset % :</label>
                    <input
                      type="number"
                      value={leftoffset}
                      onChange={e => setLeftOffset(Number(e.target.value))}
                      placeholder="Enter Left Offset"
                      className="form-control"
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group menu-card2">
                    <label>How Many Time Video repeat (0 for forever):</label>
                    <input
                      type="number"
                      value={videotime}
                      onChange={e => setVideoTime(Number(e.target.value))}
                      placeholder="Enter Video Time"
                      className="form-control"
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-12" style={{ marginTop: '40px', marginBottom: '30px' }}>
                  <SplashText
                    text={splashText || 'Splash Text'}
                    settings={settings?.globalSettings}
                  />
                </div>
                <div className="col-md-12" style={{ marginTop: '40px', marginBottom: '30px' }}>
                  <SplashText
                    text={splashTextDownload || 'Downloading...'}
                    settings={settings?.globalSettings}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-12 ">
                  <div className="form-group">
                    <ToggleSwitch
                      label="Customize Global Settings"
                      isOn={settings.enabled}
                      onChange={handleToggleChange}
                    />
                  </div>
                </div>
              </div>

              {settings?.enabled && (
                <div className="global-section">
                  {/* Global Settings */}

                  <div className="row menu-card2">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Height:</label>
                        <input
                          type="number"
                          value={settings?.globalSettings?.height ?? 0}
                          onChange={e => handleChange('height', Number(e.target.value))}
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Width % :</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={settings?.globalSettings?.width ?? 0}
                          onChange={e => handleChange('width', Number(e.target.value))}
                          className="form-control"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Text Alignment (left centre or right):</label>

                        <select
                          id="textAlign"
                          value={settings?.globalSettings?.textAlign || 'left'}
                          onChange={e => handleChange('textAlign', e.target.value)}
                          className="form-select form-control"
                        >
                          <option value="left">Left</option>
                          <option value="center">Center</option>
                          <option value="right">Right</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Fill Color */}
                  <div className="row menu-card2">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Font Family:</label>

                        <select
                          id="font"
                          value={settings?.globalSettings?.font || 'Arial'}
                          onChange={e => handleChange('font', e.target.value)}
                          className="form-select form-control"
                        >
                          {fontOptions}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Font Color:</label>
                        <MuiColorInput
                          className="form-control"
                          format="hex"
                          value={settings?.globalSettings?.fontColor || '#000000'}
                          onChange={newHex => handleChange('fontColor', newHex?.toUpperCase?.())}
                          fallbackValue="#000000"
                          // size="small"
                          isAlphaHidden={true} // This disables the alpha slider
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '0px !important',
                              height: 53,
                            },
                            '& .MuiOutlinedInput-input': {
                              height: '36px',
                            },
                            '& .MuiColorInput-Button': {
                              width: '49px !important', // Increase width
                              height: '40px !important',
                            },
                            '& .MuiInputAdornment-root .MuiBox-root': {
                              width: 18,
                              height: 18,
                            },
                          }}
                        />
                        {/* <input
                          type="color"
                          value={

                            settings?.globalSettings?.fontColor || "#000000"
                          }
                          onChange={(e) => handleChange('fontColor', e.target.value)}
                          className="form-control"
                        /> */}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Font Size:</label>
                        <input
                          type="number"
                          value={settings?.globalSettings?.fontSize || ''}
                          onChange={e => handleChange('fontSize', Number(e.target.value))}
                          className="form-control"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Shadow Settings */}

                  <div className="row menu-card2">
                    <div className="col-md-2">
                      <div className="form-group">
                        <label>Shadow angle:</label>
                        <input
                          type="number"
                          className="form-control"
                          value={settings?.globalSettings?.shadow?.angle ?? 0}
                          onChange={e => handleChange('shadow.angle', Number(e.target.value))}
                        />
                      </div>
                    </div>

                    <div className="col-md-2">
                      <div className="form-group">
                        <label>Shadow size:</label>
                        <input
                          type="number"
                          value={settings?.globalSettings?.shadow?.size ?? 0}
                          onChange={e => handleChange('shadow.size', Number(e.target.value))}
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="col-md-2">
                      <div className="form-group">
                        <label>Shadow distance:</label>
                        <input
                          type="number"
                          value={settings?.globalSettings?.shadow?.distance ?? 0}
                          onChange={e => handleChange('shadow.distance', Number(e.target.value))}
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="col-md-2">
                      <div className="form-group">
                        <label>Shadow Alpha:</label>
                        <input
                          type="number"
                          value={settings?.globalSettings?.shadow?.alpha ?? 0}
                          onChange={e => handleChange('shadow.alpha', Number(e.target.value))}
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="col-md-2">
                      <div className="form-group">
                        <label>Shadow Color:</label>
                        <MuiColorInput
                          className="form-control"
                          format="hex"
                          value={settings?.globalSettings?.shadow?.color || '#000000'}
                          onChange={newHex => handleChange('shadow.color', newHex?.toUpperCase?.())}
                          fallbackValue="#000000"
                          // size="small"
                          isAlphaHidden={true} // This disables the alpha slider
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '0px !important',
                              height: 53,
                            },
                            '& .MuiOutlinedInput-input': {
                              height: '36px',
                            },
                            '& .MuiColorInput-Button': {
                              width: '49px !important', // Increase width
                              height: '40px !important',
                            },
                            '& .MuiInputAdornment-root .MuiBox-root': {
                              width: 18,
                              height: 18,
                            },
                          }}
                        />
                        {/* <input
                          type="color"
                          value={

                            settings?.globalSettings?.shadow?.color || "#000000"
                          }
                          onChange={(e) => handleChange('shadow.color', e.target.value)}
                          className="form-control"
                        /> */}
                      </div>
                    </div>

                    <div className="col-md-2">
                      <div className="form-group">
                        <label>Highlight Font color:</label>

                        <MuiColorInput
                          className="form-control"
                          format="hex"
                          value={settings?.globalSettings?.highlight?.fontColor || '#000000'}
                          onChange={newHex =>
                            handleChange('highlight.fontColor', newHex?.toUpperCase?.())
                          }
                          fallbackValue="#000000"
                          // size="small"
                          isAlphaHidden={true} // This disables the alpha slider
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '0px !important',
                              height: 53,
                            },
                            '& .MuiOutlinedInput-input': {
                              height: '36px',
                            },
                            '& .MuiColorInput-Button': {
                              width: '49px !important', // Increase width
                              height: '40px !important',
                            },
                            '& .MuiInputAdornment-root .MuiBox-root': {
                              width: 18,
                              height: 18,
                            },
                          }}
                        />
                        {/* <input
                          type="color"
                          className="form-control"
                          value={settings?.globalSettings?.highlight?.fontColor || "#000000"}
                          onChange={(e) => handleChange('highlight.fontColor', e.target.value)}
                        /> */}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="row">
                <div className="col-md-6">
                  <button
                    type="submit"
                    onClick={handleSubmit}
                    style={{ backgroundColor: buttonColor }}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </form>
          </Box>
        </div>
      </div>
      <style jsx>{`
        .btn-back-effect {
          border: 1px solid #6c757d !important;
          color: #6c757d !important;
          background-color: transparent !important;
          border-radius: 8px !important;
        }

        .btn-back-effect:hover {
          background-color: var(--brand-color) !important;
          color: #fff !important;
          border-color: var(--brand-color) !important;
        }
      `}</style>
    </div >
  );
}

// MediaDropzone Component
const MediaDropzone = ({ onDrop, selectedFile }) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['UploadButton.jpeg', '.jpg', '.png', '.gif'],
      'video/*': ['.mp4', '.mov', '.mp3'],
      'application/UploadButtonpdf': ['.pdf', '.srt', '.vtt'],
    },
    multiple: false,
  });

  return (
    <div
      {...getRootProps()}
      style={{
        border: '2px dashed #cccccc',
        padding: '20px',
        borderRadius: '5px',
        textAlign: 'center',
        cursor: 'pointer',
      }}
    >
      <input {...getInputProps()} />
      {selectedFile ? (
        <p>{selectedFile.name}</p>
      ) : (
        <p>Drag and drop a file here, or click to select a file</p>
      )}
    </div>
  );
};

// UploadButton Component
const UploadButton = ({ uploading, onUpload, buttonColor }) => (
  <button
    onClick={onUpload}
    type="button"
    style={{
      marginTop: '20px',
      //   padding: "10px 20px",
      backgroundColor: buttonColor,
      color: '#FFFFFF',
      border: 'none',
      borderRadius: '5px',
      cursor: 'pointer',
    }}
    disabled={uploading}
  >
    {uploading ? 'Uploading...' : 'Upload'}
  </button>
);

const getFileType = fileUrl => {
  if (!fileUrl) return 'blank'; // No URL provided

  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg'];
  const videoExtensions = ['.mp4', '.mov', '.avi', '.mkv', '.webm'];

  if (imageExtensions.some(ext => fileUrl.toLowerCase().endsWith(ext))) {
    return 'image';
  }
  if (videoExtensions.some(ext => fileUrl.toLowerCase().endsWith(ext))) {
    return 'video';
  }
  return 'other'; // Other file types or unsupported extensions
};

// UploadedUrl Component
const UploadedUrl = ({ url, buttonColor, deleteFile }) =>
  url ? (
    <div
      style={{
        marginTop: '20px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start', // Adjust alignment of child elements (optional)
        gap: '10px', // Add spacing between child elements (optional)
      }}
    >
      <div style={{ float: 'left', width: '100%', marginTop: '5px' }}>
        Uploaded File:{' '}
        <a
          href={url.fileUrl}
          style={{ fontWeight: 'bold' }}
          target="_blank"
          rel="noopener noreferrer"
        >
          {url.fileName}
        </a>{' '}
        {url.fileName ? (
          <i
            class="dw dw-delete-3"
            style={{ fontSize: '18px', color: buttonColor, cursor: 'pointer' }}
            onClick={deleteFile}
          ></i>
        ) : (
          ''
        )}
      </div>
      <div style={{ float: 'right', width: '30%', textAlign: 'right', display: 'none' }}>
        {getFileType(url.fileUrl) === 'image' ? (
          // Render image if it's an image file
          <a href={url.fileUrl} target="_blank" rel="noreferrer">
            <img src={url.fileUrl} style={{ maxWidth: '200px', maxHeight: '60px' }} alt="" />
          </a>
        ) : getFileType(url.fileUrl) === 'video' ? (
          // Render play button for video file
          <a href={url.fileUrl} target="_blank" rel="noreferrer">
            <img
              src="https://paulavatar.s3.us-east-2.amazonaws.com/play-button.png"
              style={{ maxWidth: '200px', maxHeight: '60px' }}
              alt="Play Video"
            />
          </a>
        ) : (
          // Fallback for other cases
          ''
        )}
      </div>
    </div>
  ) : null;

const UploadedUrlImage = ({ url }) => {
  return (
    <>
      {url ? (
        <div style={{ margin: 'auto' }}>
          {getFileType(url.fileUrl) === 'image' ? (
            // Render image if it's an image file
            <a href={url.fileUrl} target="_blank" rel="noreferrer">
              <img src={url.fileUrl} style={{ maxWidth: '210px', maxHeight: '80px' }} alt="" />
            </a>
          ) : getFileType(url.fileUrl) === 'video' ? (
            // Render play button for video file
            <a href={url.fileUrl} target="_blank" rel="noreferrer">
              <img
                src="https://paulavatar.s3.us-east-2.amazonaws.com/play-button.png"
                style={{ maxWidth: '200px', maxHeight: '60px' }}
                alt="Play Video"
              />
            </a>
          ) : (
            // Fallback for other cases
            ''
          )}
        </div>
      ) : null}
    </>
  );
};
